import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, Phone, Calendar, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", book_demo: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-x py-20 grid md:grid-cols-2 gap-12 max-w-6xl">
      <div>
        <span className="pill">Contact</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">Let&apos;s talk wellness.</h1>
        <p className="mt-5 text-lg text-zinc-600">
          Reach out about pricing, demos, university research partnerships or anything else.
        </p>
        <div className="mt-10 space-y-5">
          <a href="mailto:hello@sonote.app" className="flex items-center gap-3 text-zinc-700">
            <span className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
              <Mail className="w-4 h-4" style={{ color: "var(--gold-hover)" }} />
            </span>
            hello@sonote.app
          </a>
          <div className="flex items-center gap-3 text-zinc-700">
            <span className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
              <Phone className="w-4 h-4" style={{ color: "var(--gold-hover)" }} />
            </span>
            +1 (888) 555-0199
          </div>
          <div className="flex items-center gap-3 text-zinc-700">
            <span className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
              <Calendar className="w-4 h-4" style={{ color: "var(--gold-hover)" }} />
            </span>
            Mon–Fri, 8am–6pm ET
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-elev space-y-4" data-testid="contact-form">
        {done ? (
          <div className="text-center py-10">
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "var(--gold)" }} />
            <h3 className="mt-4 font-bold text-xl">Thank you!</h3>
            <p className="text-zinc-600 mt-2">We&apos;ll be in touch within one business day.</p>
          </div>
        ) : (
          <>
            <div>
              <label className="text-sm font-semibold text-zinc-700">Name</label>
              <input className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-700">Email</label>
              <input type="email" className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-700">Phone (optional)</label>
              <input className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-700">Message</label>
              <textarea rows="5" className="mt-2 w-full rounded-md border border-zinc-200 p-4 focus:ring-2 focus:ring-[#d4af37] outline-none" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700">
              <input type="checkbox" checked={form.book_demo} onChange={(e) => setForm({ ...form, book_demo: e.target.checked })} data-testid="contact-demo-check" />
              I want to book a demo
            </label>
            {err && <div className="text-sm text-red-600" data-testid="contact-error">{err}</div>}
            <button disabled={submitting} className="btn-gold w-full" data-testid="contact-submit">
              {submitting ? "Sending…" : form.book_demo ? "Book a demo" : "Send message"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
